import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo } from 'react';

import { Permissions } from 'services/constants';
import {
    sendUserSupportMessage,
    setUserSupportConversation,
    setUserSupportReferral,
} from 'services/userSupportHelpers';
import { useAppDispatch, useAppSelector } from 'store';
import { useGetSupplierUserNameMapQuery } from 'store/api/authApi';
import { useGetReferralQuery } from 'store/api/performanceApi';
import { useGetPayerSegmentsQuery, useGetProductsMapQuery } from 'store/api/referenceApi';
import { useGetReferralDetailsQuery } from 'store/api/treatmentApi';
import { fetchAuthProfile } from 'store/auth/authThunks';
import {
    fetchPayerDetails,
    fetchPrescriberDetails,
    fetchSupplierDetails,
} from 'store/reference/referenceThunks';
import {
    updateCurrentReferral,
    updateReferralConversations,
} from 'store/userSupport/userSupportSlice';
import { fetchConversationFacts, registerUser } from 'store/userSupport/userSupportThunks';
import { fetchWorklistTasks } from 'store/worklist/worklistThunks';

export const usePermissionCheck = (
    permissionToCheck: (typeof Permissions)[keyof typeof Permissions],
) => {
    const permissions = useAppSelector((state) => state.auth.permissions);
    return permissions?.includes(permissionToCheck);
};

export const useReferralDetails = (referralId?: string, payerId?: string) => {
    const { data } = useGetReferralDetailsQuery(
        referralId && payerId ? { payerId, referralId } : skipToken,
    );
    return data;
};

export const usePayerDetails = (payerId?: string) => {
    const payerDetails = useAppSelector((state) => state.reference.payerDetails[payerId ?? '']);
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (payerId && !payerDetails) {
            dispatch(fetchPayerDetails(payerId));
        }
    }, [payerDetails, payerId, dispatch]);
    return payerDetails;
};

export const useSupplierDetails = (supplierNpi?: string) => {
    const supplierDetails = useAppSelector(
        (state) => state.reference.supplierDetails[supplierNpi ?? ''],
    );
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (supplierNpi && !supplierDetails) {
            dispatch(fetchSupplierDetails(supplierNpi));
        }
    }, [supplierNpi, supplierDetails, dispatch]);
    return supplierDetails;
};

export const usePrescriberDetails = (prescriberId?: string) => {
    const dispatch = useAppDispatch();
    const prescriberDetails = useAppSelector(
        (state) => state.reference.prescriberDetails[prescriberId ?? ''],
    );
    useEffect(() => {
        if (prescriberId && !prescriberDetails) {
            dispatch(fetchPrescriberDetails(prescriberId));
        }
    }, [prescriberDetails, prescriberId, dispatch]);
    return prescriberDetails;
};

export const useWorklistTasks = () => {
    const dispatch = useAppDispatch();
    const worklistTasks = useAppSelector((state) => state.worklist.worklistTasks);
    useEffect(() => {
        (() => dispatch(fetchWorklistTasks()))();
    }, [dispatch]);

    const { data: userNameMap } = useGetSupplierUserNameMapQuery();
    const { data: segments } = useGetPayerSegmentsQuery();
    const { data: productsMap } = useGetProductsMapQuery();

    const mappedTasks = useMemo(
        () =>
            worklistTasks?.map((task) => ({
                ...task,
                health_plan: segments?.payerSegmentMap?.[task.segment_id]?.name,
                action_taken_by: userNameMap?.[task.action_taken_by ?? ''] ?? task.action_taken_by,
                task_info: {
                    ...task.task_info,
                    product_name:
                        task.task_info?.product_name ??
                        productsMap?.[task.task_info?.product_service_oid ?? '']?.description,
                },
            })),
        [worklistTasks, userNameMap, segments?.payerSegmentMap, productsMap],
    );
    return mappedTasks;
};

export const useAuthProfile = (isOpen = true) => {
    const dispatch = useAppDispatch();
    const profile = useAppSelector((state) => state.auth.profile);
    useEffect(() => {
        (async () => {
            if (isOpen && !profile) {
                await dispatch(fetchAuthProfile());
            }
        })();
    }, [dispatch, isOpen, profile]);

    return profile;
};

export const useUserProfile = () => {
    const dispatch = useAppDispatch();
    const userProfile = useAppSelector((state) => state.userSupport.userProfile);
    const authProfile = useAuthProfile(true);
    useEffect(() => {
        if (authProfile && !userProfile) {
            dispatch(
                registerUser({
                    details: {
                        first_name: authProfile?.firstName,
                        last_name: authProfile?.lastName,
                    },
                }),
            );
        }
    }, [authProfile, dispatch, userProfile]);

    return userProfile;
};

export const useUserSupportMessages = () => {
    const dispatch = useAppDispatch();
    const conversationId = useAppSelector((state) => state.userSupport.currentConversation);
    const messages = useAppSelector(
        (state) => state.userSupport.conversationMessages[conversationId ?? '']?.messages,
    );
    const facts = useAppSelector(
        (state) => state.userSupport.conversationFacts[conversationId ?? ''],
    );
    useEffect(() => {
        if (conversationId) {
            dispatch(setUserSupportConversation(conversationId));
        }
    }, [conversationId, dispatch]);
    useEffect(() => {
        if (conversationId && !facts) {
            dispatch(fetchConversationFacts(conversationId));
        }
    }, [conversationId, dispatch, facts]);
    return {
        messages: messages ? Object.values(messages) : undefined,
        facts,
        sendMessage: (message: {
            conversation_id: string;
            details: { content: { message: string } };
        }) => {
            dispatch(sendUserSupportMessage(message));
        },
    };
};

export const useUserSupportConversations = (referralId?: string) => {
    const dispatch = useAppDispatch();
    const conversations = useAppSelector((state) => state.userSupport.referralConversations);
    const referralConversationCounts = useAppSelector(
        (state) => state.userSupport.referralConversationCounts?.[referralId ?? ''],
    );
    let conversationList = conversations ? Object.values(conversations) : undefined;
    if (referralConversationCounts) {
        conversationList = conversationList?.map((conversation) => ({
            ...conversation,
            isUnread:
                referralConversationCounts?.conversations[conversation.conversationId] ?? false,
        }));
    }
    useEffect(() => {
        if (referralId) {
            dispatch(setUserSupportReferral(referralId));
        }
        return () => {
            if (referralId) {
                dispatch(updateReferralConversations({ conversations: undefined }));
                dispatch(setUserSupportReferral(null));
                dispatch(updateCurrentReferral());
            }
        };
    }, [dispatch, referralId]);
    return {
        conversationList,
        referralConversationCounts,
    };
};

/** Referral could come from won or pending */
export const useSelectedReferral = ({ referralId }: { referralId: string }) => {
    const { data: supplierReferral } = useGetReferralQuery(referralId);
    const pendingReferral = useAppSelector((state) =>
        state.wonReferralList.pendingReferralData?.find(
            (referral) => referral.referralId === referralId,
        ),
    );

    return supplierReferral ? { ...supplierReferral, ...pendingReferral } : pendingReferral;
};
